import { DisplayMode } from '@rocco/types/enum/display-mode';
import {
    SocialSharingModal,
    uiStateAtomSocialSharingModalIsOpened,
} from '@rocco/ui/social-sharing/components/SocialSharingModal';
import { atom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import type { Platform } from '../utils/platform';

///// State /////

/**
 * Configuration for tracking social sharing interactions.
 * Provides callbacks for monitoring when users open the sharing modal and complete share actions.
 */
export interface SocialSharingTrackingConfig {
    /**
     * Callback fired when the social sharing modal is opened.
     * Used for tracking modal open events.
     */
    onOpen?: () => void;

    /**
     * Callback fired when a user shares content on a specific platform.
     * @param platform - The social platform used for sharing (e.g., 'facebook', 'twitter')
     */
    onShare?: (platform: Platform) => void;
}

/**
 * Context interface for managing social sharing actions and interactions.
 * Provides methods and configuration for showing the social sharing modal and tracking user interactions.
 */
interface SocialSharingActionsContextType {
    /** Current display mode (mobile or desktop) affecting how sharing UI is presented */
    displayMode: DisplayMode;

    /** Optional configuration for tracking social sharing interactions */
    trackingConfig?: SocialSharingTrackingConfig;

    /**
     * Shows the social sharing modal for the current page.
     * @returns {Promise<string>} Promise that resolves with the platform used for sharing or rejects if cancelled
     */
    showSocialSharingModal: () => Promise<string>;
}

/**
 * Atoms for managing social sharing actions context.
 * Uses Jotai for state management with a public read-only atom and a private writable atom.
 */
export const uiContextAtomSocialSharingActions = atom(get =>
    get(localContextAtom),
);
const localContextAtom = atom<SocialSharingActionsContextType>();

///// Component /////

/**
 * Props for the SocialSharingActions component.
 */
interface SocialSharingActionsProps {
    /**
     * Display mode that determines how the sharing UI is presented
     * (mobile optimizes for touch, desktop for mouse interactions)
     */
    displayMode: DisplayMode;

    /**
     * Available social platforms for sharing
     * Defaults to ['messenger', 'email', 'linkedin', 'facebook', 'twitter', 'copy']
     */
    platforms?: Platform[];

    /**
     * Configuration for tracking social sharing interactions
     * Provides callbacks for monitoring when users open the sharing modal and complete share actions
     */
    trackingConfig?: SocialSharingTrackingConfig;

    /**
     * Callback to handle sharing on a specific platform
     * @param platform - The platform to share on (e.g., 'facebook', 'twitter')
     */
    onShare?: (platform: Platform) => void;
}

/**
 * Component that manages social sharing functionality across the application.
 *
 * This component:
 * 1. Provides a global context for sharing operations via Jotai atoms
 * 2. Renders the social sharing modal (hidden by default)
 * 3. Manages the promise-based API for showing the modal and handling share actions
 * 4. Tracks sharing interactions through the provided tracking configuration
 *
 * Usage:
 * - Place this component once at a high level in your application
 * - Access the sharing functionality anywhere using the uiContextAtomSocialSharingActions atom
 *
 * @example
 * // In your app root:
 * <SocialSharingActions
 *   displayMode={DisplayMode.Desktop}
 *   trackingConfig={{
 *     onOpen: () => console.log('Modal opened'),
 *     onShare: (platform) => console.log(`Shared on ${platform}`)
 *   }}
 * />
 *
 * // Elsewhere in your app:
 * const socialSharingActions = useAtomValue(uiContextAtomSocialSharingActions);
 * const handleShareClick = async () => {
 *   try {
 *     const platform = await socialSharingActions.showSocialSharingModal();
 *     console.log(`User shared on ${platform}`);
 *   } catch {
 *     console.log('User cancelled sharing');
 *   }
 * };
 */
export const SocialSharingActions = (props: SocialSharingActionsProps) => {
    const {
        displayMode,
        platforms = ['email', 'linkedin', 'facebook', 'twitter', 'copy'],
        onShare,
        trackingConfig,
    } = props;

    const setSocialSharingModalOpened = useSetAtom(
        uiStateAtomSocialSharingModalIsOpened,
    );
    const setContext = useSetAtom(localContextAtom);

    /**
     * Social sharing modal
     */
    const socialSharingPromiseRef = useRef<{
        resolve: (platform: string) => void;
        reject: () => void;
    } | null>(null);

    const showSocialSharingModal = useCallback(() => {
        trackingConfig?.onOpen?.();

        setSocialSharingModalOpened(true);

        return new Promise<string>((resolve, reject) => {
            socialSharingPromiseRef.current = { resolve, reject };
        });
    }, [setSocialSharingModalOpened, trackingConfig]);

    /**
     * Handle sharing on a specific platform
     */
    const handleShare = useCallback(
        (platform: Platform) => {
            // Track the share event
            trackingConfig?.onShare?.(platform);

            // Call the onShare callback if provided
            onShare?.(platform);

            // Resolve the promise with the platform
            socialSharingPromiseRef.current?.resolve(platform);

            // Close the modal
            setSocialSharingModalOpened(false);
        },
        [onShare, setSocialSharingModalOpened, trackingConfig],
    );

    /**
     * Provide context
     */
    useEffect(() => {
        const providerValue = {
            displayMode,
            showSocialSharingModal,
            trackingConfig,
        };

        setContext(providerValue); // jotai atom setter
    }, [setContext, displayMode, showSocialSharingModal, trackingConfig]);

    return (
        <SocialSharingModal
            displayMode={displayMode}
            platforms={platforms}
            onShare={handleShare}
            onCancel={() => {
                socialSharingPromiseRef.current?.reject();
                setSocialSharingModalOpened(false);
            }}
        />
    );
};
